import React from "react";

import { separateInputWrapperPropsAndInputProps } from "../../../../../organisms/Form/utils";

import { DatePickerWithRangeProps, DateRange, DateRangePicker } from "../../../../../molecules/DateRangePicker";
import { usePyForm } from "../../../context";
import { BaseFormFieldProps, FormField } from "../../FormComponents/FormField";

// Get all unique props from DateRangePicker that aren't in BaseFormFieldProps
type UniqueInputProps = Omit<
	DatePickerWithRangeProps,
	keyof BaseFormFieldProps<DateRange | undefined> | "value" | "onChange"
>;

export type FormDateRangePickerProps = Omit<BaseFormFieldProps<DateRange | undefined>, "input"> & UniqueInputProps;

/**
 * This is a form component that is used to create a date range picker field.
 * It is a wrapper around the DateRangePicker component.
 * It is used to create a date range picker field that is connected to the PyForm context.
 * Its props cross-cut with the DateRangePicker component and the FormField component.
 *
 * DateRangePicker is a component that allows users to select a range of dates.
 * It is a controlled component that uses the value and onChange props to update the date range value.
 */
export const FormDateRangePicker = (props: FormDateRangePickerProps) => {
	const form = usePyForm();
	const { inputWrapperProps, inputProps } = separateInputWrapperPropsAndInputProps(props, form.id);

	if (!form) {
		throw new Error("FormDateRangePicker must be used within a Form component");
	}

	return (
		<FormField<DateRange | undefined>
			{...inputWrapperProps}
			input={(field) => {
				return (
					<DateRangePicker
						{...inputProps}
						value={field.value}
						onChange={field.onChange}
						id={inputWrapperProps.id}
						data-testid={`${inputWrapperProps["data-testid"]}-date-range-picker`}
						disabled={inputWrapperProps.disabled}
					/>
				);
			}}
		/>
	);
};

FormDateRangePicker.displayName = "FormDateRangePicker";

export default FormDateRangePicker;
